import React, {useEffect, useState} from "react";
import {useForm} from "common/src/hooks/form/formHook";
import Input from "common/src/components/widget/Input";
import {POSITIVE_NUMBER} from "common/src/utils/NumberUtils";
import {useNotifications} from "common/src/hooks/notification/notificationHook";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import DateTimePicker from "common/src/components/widget/DateTimePicker";
import * as yup from "yup";
import GuestInput from "common/src/components/widget/GuestInput";
import CheckboxGroup from "common/src/components/widget/CheckboxGroup";
import Checkbox from "common/src/components/widget/Checkbox";
import Button from "common/src/components/widget/Button";
import TextArea from "common/src/components/widget/TextArea";
import ApiClient from "common/src/api/ApiClient";
import PromiseBuilder from "common/src/utils/PromiseUtils";
import Select from "common/src/components/widget/Select";
import {BoardType} from "../../../model/boardType";
import {AccommodationCategory} from "../../../model/accommodationCategory";
import {yesterday} from "common/src/utils/DateUtils";
import FormContainer from "common/src/components/form/FormContainer";

function RequestFormPage() {
  const boardTypes = BoardType();
  const accommodationCategories = AccommodationCategory();
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {
    addSuccessNotification,
    addErrorNotification,
  } = useNotifications();

  const [isLoading, setIsLoading] = useState(false);
  const schema = {
    startDate: yup.date().min(yesterday, t('date_from_today')),
    endDate: yup.date().when('startDate', {
      is: (startDate) => startDate != null,
      then: yup.date().test({
        name: 'is-after-start-date',
        exclusive: true,
        message: t('return_date_after_start_date'),
        test: function (endDate) {
          const { startDate } = this.parent;
          if(endDate == null) {
            return true;
          }
          return endDate > startDate;
        },
      }),
    }),
    trailService: yup.object().shape({
      guide: yup.boolean(),
      mobilityService: yup.boolean(),
      backTransfer: yup.boolean(),
      luggageTransport: yup.boolean(),
    }),
    deal: yup.string().max(256, t('length_exceeded_error')),
    noteFromClient: yup.string().max(512, t('length_exceeded_error')),
    numberOfAdults: yup.number().nullable().min(0, t('number_between_min_max', {min: 0, max: 100})).max(100, t('number_between_min_max', {min: 0, max: 100})),
    numberOfChildren: yup.number().nullable().min(0, t('number_between_min_max', {min: 0, max: 100})).max(100, t('number_between_min_max', {min: 0, max: 100})),
    numberOfRooms: yup.number().nullable().min(1, t('number_between_min_max', {min: 1, max: 100})).max(100, t('number_between_min_max', {min: 0, max: 100})),
    boardType: yup.string(),
    accommodationCategory: yup.string(),
    guest: yup.object().shape({
      firstName: yup.string().required(t('required_field')),
      lastName: yup.string().required(t('required_field')),
      mobileNumber: yup.string().required(t('required_field')),
      email: yup.string().email(t('email_not_valid')).required(t('required_field')),
    })
  };

  const {
    values,
    getField,
    handleSubmit,
    isEdited,
    setFormNotDirty,
    triggerValidation
  } = useForm(createRequest, {}, schema);

  useEffect(() => {
    if(values.endDate){
      triggerValidation('endDate');
    }
  }, [values.startDate]);

  function createRequest() {
    let request = async () => ApiClient.post("/api/v1/booking/request", createRequestDataObject())
    setIsLoading(true);
    new PromiseBuilder()
      .then(request)
      .then(() => {
        setIsLoading(false);
        addSuccessNotification(t('toast.create_success'));
        setFormNotDirty();
      })
      .then(() => {
        navigate('/request');
      })
      .catch(async (res) => {
        setIsLoading(false);
        addErrorNotification(t("toast.create_failed_status", {status: res.response.statusText}), t("toast.error"))
      })
      .run();
  }

  function createRequestDataObject() {
    return {
      guest: {
        name: values.guest?.firstName,
        surname: values.guest?.lastName,
        mobileNumber: values.guest?.mobileNumber,
        email: values.guest?.email,
      },
      trailService: {
        luggageTransport: values.trailService?.luggageTransport ?? false,
        guide: values.trailService?.guide ?? false,
        backTransfer: values.trailService?.backTransfer ?? false,
        mobilityService: values.trailService?.mobilityService ?? false,
      },
      startDate: values.startDate,
      endDate: values.endDate,
      numberOfAdults: values.numberOfAdults,
      numberOfChildren: values.numberOfChildren,
      numberOfRooms: values.numberOfRooms,
      deal: values.deal,
      noteFromClient: values.noteFromClient,
      board: values.board,
      accommodationCategory: values.accommodationCategory,
    }
  }

  return (
    <div>
      <FormContainer onSubmit={handleSubmit} isEdited={isEdited}>
        <div className="card">
          <header className="card-header">
            <p className="card-header-title">
              <span className="icon fz fz-sign-out-alt medium mr-1"/>
              <span>{t("request.create_request")}</span>
            </p>
          </header>
          <div className="card-content">
            <div className="columns">
              <div className="column is-one-quarter">
                <DateTimePicker label={t('request.start_date')}
                                formField={getField("startDate")}
                />
                <DateTimePicker label={t('request.end_date')}
                                formField={getField("endDate")}
                />
              </div>
              <div className="column is-one-quarter">
                <Select label={t("request.board")}
                        options={boardTypes}
                        formField={getField("board")}/>
                <Select label={t("request.accommodation_category")}
                        options={accommodationCategories}
                        formField={getField("accommodationCategory")}/>
              </div>
              <div className="column is-one-quarter">
                <TextArea
                  label={t('request.deal')}
                  formField={getField("deal")}
                  rows={3}
                />

              </div>
              <div className="column is-one-quarter">
                <TextArea
                  label={t('request.note_from_client')}
                  formField={getField("noteFromClient")}
                  rows={3}
                />
              </div>
            </div>
            <div className="columns">
              <div className="column is-one-quarter">
                <Input
                  className="has-no-margin-top"
                  label={t('request.number_of_adults')}
                  formField={getField("numberOfAdults")}
                  type={"number"}
                  numberFormat={POSITIVE_NUMBER}
                  showArrows={true}
                />
              </div>
              <div className="column is-one-quarter">
                <Input
                  className="has-no-margin-top"
                  label={t('request.number_of_children')}
                  formField={getField("numberOfChildren")}
                  type={"number"}
                  numberFormat={POSITIVE_NUMBER}
                  showArrows={true}
                />
              </div>
              <div className="column is-one-quarter">
                <Input
                  className="has-no-margin-top"
                  label={t('request.number_of_rooms')}
                  formField={getField("numberOfRooms")}
                  type={"number"}
                  numberFormat={POSITIVE_NUMBER}
                  showArrows={true}
                />
              </div>
            </div>
            <CheckboxGroup formField={getField("trailService")}>
              <div className="has-text-grey">{t('offer.trail_service')}</div>
              <div className="columns is-grouped is-grouped-left">
                <Checkbox
                  label={t('request.guide')}
                  type="form"
                  checked={values.trailService?.guide}
                  color="auto"
                  formField={getField("trailService.guide")}
                />
                <Checkbox
                  label={t('request.back_transfer')}
                  type="form"
                  checked={values.trailService?.backTransfer}
                  color="auto"
                  formField={getField("trailService.backTransfer")}
                />
                <Checkbox
                  label={t('request.luggage_transfer')}
                  type="form"
                  checked={values.trailService?.luggageTransport}
                  color="auto"
                  formField={getField("trailService.luggageTransport")}
                />
              </div>
            </CheckboxGroup>
          </div>
        </div>
        <div className="card">
          <header className="card-header">
            <p className="card-header-title">
              <span className="icon fz fz-book-user medium mr-1"/>
              <span>{t('client')}</span>
            </p>
          </header>
          <div className="card-content">
            <GuestInput
              firstName={getField("guest.firstName")}
              lastName={getField("guest.lastName")}
              mobileNumber={getField("guest.mobileNumber")}
              email={getField("guest.email")}
              disabled={false}
            />
          </div>
        </div>
        <Button
          isSubmit={true}
          isLoading={isLoading}
          color="primary"
          label={t('request.create')}
        />
        <Button
          color="primary"
          label={t('request.cancel')}
          isOutlined={true}
          onClick={() => {
            navigate('/request')
          }
          }/>
      </FormContainer>
    </div>
  )
}

export default RequestFormPage;
